import { leaveAPI } from '@/utils/api'
import storageService from '@/services/storageService'

export default {
  namespaced: true,
  
  state: {
    leaveRequests: [],
    leaveTypes: [],
    leaveHistory: [],
    pendingLeaves: [],
    leaveForApproval: [],
    leaveAllowance: [],
    workShifts: [],
    loading: false,
    error: null
  },
  
  mutations: {
    SET_LEAVE_REQUESTS(state, requests) {
      state.leaveRequests = requests
    },
    SET_LEAVE_TYPES(state, types) {
      state.leaveTypes = types
    },
    SET_LEAVE_HISTORY(state, history) {
      state.leaveHistory = history
    },
    SET_PENDING_LEAVES(state, leaves) {
      state.pendingLeaves = leaves
    },
    SET_LEAVE_FOR_APPROVAL(state, leaves) {
      state.leaveForApproval = leaves
    },
    SET_LEAVE_ALLOWANCE(state, allowance) {
      state.leaveAllowance = allowance
    },
    SET_WORK_SHIFTS(state, shifts) {
      state.workShifts = shifts
    },
    ADD_LEAVE_REQUEST(state, request) {
      state.leaveRequests.unshift(request)
    },
    UPDATE_LEAVE_REQUEST(state, updatedRequest) {
      const index = state.leaveRequests.findIndex(req => req.ID === updatedRequest.ID)
      if (index !== -1) {
        state.leaveRequests.splice(index, 1, updatedRequest)
      }
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    }
  },
  
  actions: {
    async getLeaveTypes({ commit }) {
      commit('SET_LOADING', true)
      
      try {
        console.log('Calling mobileGetLeaveTypes API...')
        const response = await leaveAPI.mobileGetLeaveTypes()
        console.log('Leave types API response:', response)
        
        if (response.model) {
          console.log('Setting leave types:', response.model)
          commit('SET_LEAVE_TYPES', response.model)
        } else {
          console.log('No model data in leave types response')
        }
        return response
      } catch (error) {
        console.error('Error loading leave types:', error)
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async getWorkShifts({ commit }) {
      commit('SET_LOADING', true)
      
      try {
        const response = await leaveAPI.mobileGetWorkShifts()
        if (response.model) {
          commit('SET_WORK_SHIFTS', response.model)
        }
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async takeLeave({ commit }, leaveData) {
      commit('SET_LOADING', true)
      
      try {
        // Handle document uploads if any
        let uploadedDocuments = []
        if (leaveData.documents && leaveData.documents.length > 0) {
          console.log('Uploading documents for leave request...')
          
          for (const doc of leaveData.documents) {
            try {
              // Use unified storage service
              const uploadResult = await storageService.uploadFile(doc.file, {
                documentType: doc.documentType || 2, // Default to LeaveAttachment
                userId: leaveData.userId || leaveData.empId,
                documentName: doc.documentName,
                description: doc.description || 'Leave attachment'
              })
              
              console.log('Document upload result:', uploadResult)
              
              if (uploadResult.success) {
                uploadedDocuments.push({
                  documentId: uploadResult.id,
                  documentName: uploadResult.fileName,
                  documentType: uploadResult.documentType,
                  filePath: uploadResult.filePath,
                  fileUrl: uploadResult.fileUrl,
                  storageMethod: uploadResult.storageMethod
                })
              }
            } catch (uploadError) {
              console.error('Failed to upload document:', doc.documentName, uploadError)
              // Continue with other documents even if one fails
            }
          }
        }
        
        // Remove documents from leaveData before sending to API
        const { documents, ...leaveRequestData } = leaveData
        
        // Add uploaded document references to leave request
        if (uploadedDocuments.length > 0) {
          leaveRequestData.attachments = uploadedDocuments
        }
        
        console.log('Submitting leave request with data:', leaveRequestData)
        const response = await leaveAPI.mobileTakeLeave(leaveRequestData)
        
        if (response.model) {
          commit('ADD_LEAVE_REQUEST', response.model)
        }
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async getLeaveHistory({ commit }, empId) {
      commit('SET_LOADING', true)
      
      try {
        console.log('Fetching leave history for employee:', empId)
        const response = await leaveAPI.mobileGetLeaveHistory(empId)
        console.log('Leave history API response:', response)
        
        if (response.model) {
          console.log('Setting leave history in store:', response.model)
          commit('SET_LEAVE_HISTORY', response.model)
        } else {
          console.log('No model data in leave history response')
          commit('SET_LEAVE_HISTORY', [])
        }
        return response
      } catch (error) {
        console.error('Error loading leave history:', error)
        commit('SET_ERROR', error.message)
        commit('SET_LEAVE_HISTORY', [])
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async getPendingLeaves({ commit }, empId) {
      commit('SET_LOADING', true)
      
      try {
        const response = await leaveAPI.mobileGetPendingLeaves(empId)
        if (response.model) {
          commit('SET_PENDING_LEAVES', response.model)
        }
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async getLeaveForApproval({ commit }, empId) {
      commit('SET_LOADING', true)
      
      try {
        const response = await leaveAPI.mobileGetLeaveForApproval(empId)
        if (response.model) {
          commit('SET_LEAVE_FOR_APPROVAL', response.model)
        }
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async approveLeave({ commit }, approvalData) {
      commit('SET_LOADING', true)
      
      try {
        const response = await leaveAPI.mobileApproveLeave(approvalData)
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async cancelLeave({ commit }, leaveId) {
      commit('SET_LOADING', true)
      
      try {
        const response = await leaveAPI.mobileCancelLeave(leaveId)
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async getLeaveAllowance({ commit }, { empId, yearId }) {
      commit('SET_LOADING', true)
      
      try {
        console.log('Fetching leave allowance for employee:', empId, 'year:', yearId)
        const response = await leaveAPI.mobileGetLeaveAllowance(empId, yearId)
        console.log('Leave allowance API response:', response)
        
        if (response.model) {
          console.log('Setting leave allowance in store:', response.model)
          commit('SET_LEAVE_ALLOWANCE', response.model)
        } else {
          console.log('No model data in leave allowance response')
          commit('SET_LEAVE_ALLOWANCE', [])
        }
        return response
      } catch (error) {
        console.error('Error loading leave allowance:', error)
        commit('SET_ERROR', error.message)
        commit('SET_LEAVE_ALLOWANCE', [])
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    clearError({ commit }) {
      commit('SET_ERROR', null)
    }
  },
  
  getters: {
    leaveRequests: state => state.leaveRequests,
    leaveTypes: state => state.leaveTypes,
    leaveHistory: state => state.leaveHistory,
    pendingLeaves: state => state.pendingLeaves,
    leaveForApproval: state => state.leaveForApproval,
    leaveAllowance: state => state.leaveAllowance,
    workShifts: state => state.workShifts,
    loading: state => state.loading,
    error: state => state.error,
    approvedLeaves: state => state.leaveHistory.filter(leave => leave.Status === 1),
    rejectedLeaves: state => state.leaveHistory.filter(leave => leave.Status === 2),
    pendingLeavesCount: state => state.pendingLeaves.length,
    approvalLeavesCount: state => state.leaveForApproval.length
  }
} 